/**
 * usePanelFocus - Hook for managing panel focus state
 */

import { useState, useCallback } from 'react';
import type {
  PanelEventEmitter,
  PanelFocusEventPayload,
  PanelBlurEventPayload,
} from '@principal-ade/panel-framework-core';
import type { PanelSlotId, PanelFocusState, PanelFocusActions } from '../types';
import type { PanelCollapsed } from '../../types/persistence.types';

export interface UsePanelFocusOptions {
  /** Initial focused panel (optional) */
  initialFocus?: PanelSlotId | null;

  /** Collapsed state to determine which panels are visible */
  collapsed?: PanelCollapsed;

  /** Whether this is a two-panel or three-panel layout */
  panelType?: 'two-panel' | 'three-panel';

  /** Callback when focus changes */
  onFocusChange?: (panel: PanelSlotId | null) => void;

  /** Event emitter from panel framework (optional, for focus events) */
  events?: PanelEventEmitter;

  /** Function to map panel slot to panel ID (optional, required for event emission) */
  getPanelId?: (slot: PanelSlotId) => string | undefined;
}

export interface UsePanelFocusReturn extends PanelFocusState, PanelFocusActions {
  /** Check if a specific panel is focused */
  isFocused: (panel: PanelSlotId) => boolean;
}

/**
 * Hook for managing panel focus state with keyboard shortcuts
 */
export function usePanelFocus(
  options: UsePanelFocusOptions = {}
): UsePanelFocusReturn {
  const {
    initialFocus = null,
    collapsed = {},
    panelType = 'three-panel',
    onFocusChange,
    events,
    getPanelId,
  } = options;

  const [focusedPanel, setFocusedPanelState] = useState<PanelSlotId | null>(
    initialFocus
  );

  const setFocus = useCallback(
    (panel: PanelSlotId) => {
      const previousPanel = focusedPanel;
      setFocusedPanelState(panel);
      onFocusChange?.(panel);

      // Emit framework events if event emitter is provided
      if (events && getPanelId) {
        // Emit blur event for previous panel
        if (previousPanel) {
          const prevPanelId = getPanelId(previousPanel);
          if (prevPanelId) {
            events.emit<PanelBlurEventPayload>({
              type: 'panel:blur',
              source: 'panel-layouts',
              timestamp: Date.now(),
              payload: {
                panelId: prevPanelId,
                panelSlot: previousPanel,
                nextPanelId: getPanelId(panel),
              },
            });
          }
        }

        // Emit focus event for new panel
        const panelId = getPanelId(panel);
        if (panelId) {
          events.emit<PanelFocusEventPayload>({
            type: 'panel:focus',
            source: 'panel-layouts',
            timestamp: Date.now(),
            payload: {
              panelId,
              panelSlot: panel,
              source: 'keyboard-shortcut',
            },
          });
        }
      }
    },
    [focusedPanel, onFocusChange, events, getPanelId]
  );

  const clearFocus = useCallback(() => {
    const previousPanel = focusedPanel;
    setFocusedPanelState(null);
    onFocusChange?.(null);

    // Emit blur event when clearing focus
    if (events && getPanelId && previousPanel) {
      const prevPanelId = getPanelId(previousPanel);
      if (prevPanelId) {
        events.emit<PanelBlurEventPayload>({
          type: 'panel:blur',
          source: 'panel-layouts',
          timestamp: Date.now(),
          payload: {
            panelId: prevPanelId,
            panelSlot: previousPanel,
          },
        });
      }
    }
  }, [focusedPanel, onFocusChange, events, getPanelId]);

  const getVisiblePanels = useCallback((): PanelSlotId[] => {
    const panels: PanelSlotId[] = [];

    if (!collapsed.left) {
      panels.push('left');
    }
    panels.push('middle'); // Middle is always visible
    if (panelType === 'three-panel' && !collapsed.right) {
      panels.push('right');
    }

    return panels;
  }, [collapsed, panelType]);

  const focusNext = useCallback(() => {
    const visiblePanels = getVisiblePanels();
    if (visiblePanels.length === 0) return;

    if (focusedPanel === null) {
      // No panel focused, focus first visible panel
      setFocus(visiblePanels[0]);
      return;
    }

    const currentIndex = visiblePanels.indexOf(focusedPanel);
    if (currentIndex === -1) {
      // Current panel is not visible, focus first visible panel
      setFocus(visiblePanels[0]);
      return;
    }

    // Move to next panel (wrap around)
    const nextIndex = (currentIndex + 1) % visiblePanels.length;
    setFocus(visiblePanels[nextIndex]);
  }, [focusedPanel, getVisiblePanels, setFocus]);

  const focusPrevious = useCallback(() => {
    const visiblePanels = getVisiblePanels();
    if (visiblePanels.length === 0) return;

    if (focusedPanel === null) {
      // No panel focused, focus last visible panel
      setFocus(visiblePanels[visiblePanels.length - 1]);
      return;
    }

    const currentIndex = visiblePanels.indexOf(focusedPanel);
    if (currentIndex === -1) {
      // Current panel is not visible, focus last visible panel
      setFocus(visiblePanels[visiblePanels.length - 1]);
      return;
    }

    // Move to previous panel (wrap around)
    const prevIndex =
      currentIndex === 0 ? visiblePanels.length - 1 : currentIndex - 1;
    setFocus(visiblePanels[prevIndex]);
  }, [focusedPanel, getVisiblePanels, setFocus]);

  const isFocused = useCallback(
    (panel: PanelSlotId) => focusedPanel === panel,
    [focusedPanel]
  );

  return {
    focusedPanel,
    setFocus,
    clearFocus,
    focusNext,
    focusPrevious,
    isFocused,
  };
}
